/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import pendo, { LEARNER_API_KEY } from 'js/lib/pendo';
import user from 'js/lib/user';

import { roleToCollectionsSlugMapping } from 'bundles/enterprise-career-academy/constants';
import type { RoleCollectionSlugs } from 'bundles/enterprise-career-academy/constants';
import { DISABLE_PENDO_ORGS } from 'bundles/enterprise-legacy-learner-home/constants/ProgramHomeConstants';
import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';
import Skills from 'bundles/unified-career-academy/components/Skills';
import EnterpriseDomainSelectorNotConfigurableModule from 'bundles/unified-career-academy/components/modules/EnterpriseDomainSelectorNotConfigurableModule';
import { EnterpriseForCreditCollectionNonConfigurable } from 'bundles/unified-career-academy/components/modules/EnterpriseForCreditCollectionNonConfigurable';
import EnterpriseHeroNotConfigurable from 'bundles/unified-career-academy/components/modules/EnterpriseHeroNotConfigurable';
import CareerCardCredentialsNoLink from 'bundles/unified-career-academy/components/modules/careers/CareerCardCredentialsNoLink';
import CareerCardDescription from 'bundles/unified-career-academy/components/modules/careers/CareerCardDescription';
import CareerCardModule from 'bundles/unified-career-academy/components/modules/careers/CareerCardModule';
import CareerCardStats from 'bundles/unified-career-academy/components/modules/careers/CareerCardStats';
import CareerJourneyModule from 'bundles/unified-career-academy/components/modules/careers/CareerJourneyModule';
import CareersFooter from 'bundles/unified-career-academy/components/modules/careers/CareersFooter';
import CareersModule from 'bundles/unified-career-academy/components/modules/careers/CareersModule';
import TestimonialHeadshot from 'bundles/unified-career-academy/components/modules/testimonials/TestimonialHeadshot';
import TestimonialVideo from 'bundles/unified-career-academy/components/modules/testimonials/TestimonialVideo';
import TestimonialsModule from 'bundles/unified-career-academy/components/modules/testimonials/TestimonialsModule';
import TestimonialAuthor from 'bundles/unified-career-academy/components/modules/testimonials/quote/TestimonialAuthor';
import TestimonialQuoteModule from 'bundles/unified-career-academy/components/modules/testimonials/quote/TestimonialQuoteModule';
import { useCareerAcademyVariant } from 'bundles/unified-career-academy/contexts/CareerAcademyVariantContext';
import { usePageContent } from 'bundles/unified-career-academy/contexts/PageContentContext';
import { useProgramCurriculum } from 'bundles/unified-career-academy/contexts/ProgramCurriculumContext';
import { filterEnterpriseCertificates } from 'bundles/unified-career-academy/utils/helpers';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useProgram, useProgramMembership } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';

const MIN_NUM_ROLES_TO_SHOW_DOMAIN_FILTER = 6;

const EnterprisePageRootContent = () => {
  const pageContent = usePageContent();
  const program = useProgram();
  const curriculum = useProgramCurriculum();
  const features = useEnterpriseProductFeatures();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const careerAcademyVariant = useCareerAcademyVariant();
  const programMembership = useProgramMembership();
  const thirdPartyOrg = useThirdPartyOrganization();

  const programSlug = program?.metadata?.slug ?? '';

  React.useEffect(() => {
    // Init Pendo
    if (!DISABLE_PENDO_ORGS.includes(thirdPartyOrg?.id || '')) {
      const { external_id: externalId, is_staff: isStaff, is_superuser: isSuperuser } = user.get();

      pendo.init(
        {
          visitor: {
            id: externalId,
            isStaff,
            isSuperuser,
            isLoggedIn: true,
            isCourserian: user.isCourserian(),
            isProgramMember: Boolean(programMembership.isProgramMember),
            isProgramAdmin: Boolean(programMembership.isProgramAdmin),
            programSlug,
          },
          account: {
            id: thirdPartyOrg?.id ?? '',
            name: thirdPartyOrg?.name ?? '',
            slug: thirdPartyOrg?.slug ?? '',
            isEnterprise: true,
            programSlug,
            isCareerAcademyV2: true,
          },
        },
        LEARNER_API_KEY
      );
    }
  }, [
    programMembership.isProgramAdmin,
    programMembership.isProgramMember,
    programSlug,
    thirdPartyOrg?.id,
    thirdPartyOrg?.name,
    thirdPartyOrg?.slug,
  ]);

  // enterprise admins now have the ability to select roles; this ensures we only display career cards that exist in their program's curriculum
  const filterCurriculumTracks = () => {
    if (curriculum?.__typename === 'ProgramsV1_tracksCurriculumMember') {
      const isSmallSubset =
        (curriculum?.tracksCurriculum?.coreTracks ?? []).filter(
          (track) => track.curriculumItems.filter((item) => item.__typename === 'ProgramsV1_domainIdMember').length > 0
        ).length === 0;

      const tracks =
        (isSmallSubset ? curriculum.tracksCurriculum.coreTracks : curriculum.tracksCurriculum.electiveTracks) ?? [];

      return tracks.filter((track) => track?.title?.toLowerCase().startsWith('career academy:') && track.isLocked);
    } else if (curriculum?.wildcardCurriculum) {
      return curriculum.wildcardCurriculum?.electiveTracks?.filter(
        (track) => track?.title?.toLowerCase().startsWith('career academy:') && track.isLocked
      );
    }
    return [];
  };

  const selectedRoles =
    pageContent?.careersCardsCollection?.items.filter((card) =>
      curriculum
        ? filterCurriculumTracks().some(
            (track) =>
              // Steven TODO: update using track.roleId and checking agains the Contentful slug
              track.title ===
              roleToCollectionsSlugMapping[card?.slug as (typeof RoleCollectionSlugs)[keyof typeof RoleCollectionSlugs]]
          )
        : true
    ) ?? [];

  if (!pageContent || !program || !thirdPartyOrganization) {
    return null;
  }

  const showSimplifiedNavWithExplore = GrowthDiscoveryEpicClient.get('newEnterpriseNav');

  return (
    <React.Fragment>
      {!showSimplifiedNavWithExplore && !features?.isCareerAcademyLimitedCatalogProgram && (
        <EnterpriseDomainSelectorNotConfigurableModule
          programId={program?.id}
          thirdPartyOrganizationId={program?.thirdPartyOrganizationId}
          enableSkillsInSearchAndBrowse={Boolean(features?.enableSkillsInSearchAndBrowse)}
        />
      )}
      <EnterpriseHeroNotConfigurable />
      <EnterpriseForCreditCollectionNonConfigurable programId={program?.id} />
      <CareerJourneyModule />
      <CareersModule
        title={pageContent.careersTitle}
        subtitle={pageContent.careersSubtitle}
        showDomainFilter={selectedRoles.length >= MIN_NUM_ROLES_TO_SHOW_DOMAIN_FILTER}
        orgName={thirdPartyOrganization.name}
        footer={
          features?.enableCareerAcademyLaborMarketData && <CareersFooter content={pageContent.careersFootnotes} />
        }
        careerCardsWithRoles={selectedRoles.map((card) => ({
          role: card?.jobCategory,
          card: (
            <CareerCardModule
              roleSlug={card?.slug}
              title={card?.title}
              url={card?.slug && `/career-academy/programs/${program?.metadata.slug}/roles/${card.slug}`}
              image={card?.image}
              description={<CareerCardDescription content={card?.description} />}
              skills={<Skills skills={card?.skills} />}
              careerStats={
                features?.enableCareerAcademyLaborMarketData && <CareerCardStats stats={card?.careerStats} />
              }
              credentials={
                <CareerCardCredentialsNoLink
                  credentials={filterEnterpriseCertificates(card?.programsCollection?.items, careerAcademyVariant)}
                />
              }
            />
          ),
        }))}
      />
      {features?.enableCareerAcademyTestimonialsComponent && (
        <TestimonialsModule
          title={pageContent.testimonialsTitle}
          subtitle={pageContent.testimonialsSubtitle}
          headshots={
            pageContent.testimonialsUserTestimonialCollection?.items &&
            pageContent.testimonialsUserTestimonialCollection?.items
              .filter((testimonial) => !testimonial?.videoEmbedUrl)
              .slice(0, 6)
              .map((testimonial, index) => (
                <TestimonialHeadshot
                  name={testimonial?.authorName}
                  imageUrl={testimonial?.image?.url}
                  countryName={testimonial?.authorOrganization}
                  index={index}
                />
              ))
          }
          testimonials={[
            // Create video testimonials if item has a video embed URL.
            ...((pageContent.testimonialsUserTestimonialCollection?.items &&
              pageContent.testimonialsUserTestimonialCollection.items
                .filter((testimonial) => !!testimonial?.videoEmbedUrl)
                .map((testimonial, index) => (
                  <TestimonialVideo
                    videoEmbedUrl={testimonial?.videoEmbedUrl}
                    index={index}
                    imageUrl={testimonial?.videoPlaceholderImage?.url || testimonial?.image?.url}
                    authorName={testimonial?.authorName}
                    authorTitle={testimonial?.authorTitle}
                  />
                ))) ||
              []),
            // Create TestimonialQuote testimonials if item doesn't have a video embed URL.
            ...((pageContent.testimonialsUserTestimonialCollection?.items &&
              pageContent.testimonialsUserTestimonialCollection.items
                .filter((testimonial) => !testimonial?.videoEmbedUrl)
                .map((testimonial, index) => (
                  <TestimonialQuoteModule
                    index={index}
                    text={testimonial?.text}
                    author={
                      <TestimonialAuthor
                        authorName={testimonial?.authorName}
                        authorTitle={testimonial?.authorTitle}
                        imageUrl={testimonial?.image?.url}
                      />
                    }
                  />
                ))) ||
              []),
          ]}
        />
      )}
      <CourseraMetatags
        metaNameAndProperties={{
          title: program?.metadata.name,
        }}
      />
    </React.Fragment>
  );
};

export default EnterprisePageRootContent;
